if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.hideVariantsEnabled = this.dataset.hideVariants === 'true';
        this.variantFeaturedMediaIds = new Set(
          (this.dataset.variantFeaturedMediaIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]')?.dataset?.mediaId;
        this.applyForcedVariantFilter(activeMedia);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.applyForcedVariantFilter(mediaId);
      }

      getRawMediaId(sectionMediaId) {
        if (!sectionMediaId) return '';
        const splitIndex = sectionMediaId.lastIndexOf('-');
        if (splitIndex === -1) return sectionMediaId;
        return sectionMediaId.slice(splitIndex + 1);
      }

      applyForcedVariantFilter(sectionMediaId) {
        if (!this.hideVariantsEnabled || this.variantFeaturedMediaIds.size === 0) return;

        const activeRawMediaId = this.getRawMediaId(sectionMediaId);
        if (!activeRawMediaId) return;

        const isAllowedMedia = (rawMediaId) =>
          !this.variantFeaturedMediaIds.has(rawMediaId) || rawMediaId === activeRawMediaId;

        const applyVisibility = (allowPredicate) => {
          this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((item) => {
            const rawMediaId = this.getRawMediaId(item.dataset.mediaId);
            item.toggleAttribute('hidden', !allowPredicate(item, rawMediaId));
          });

          if (this.elements.thumbnails) {
            this.elements.thumbnails.querySelectorAll('li[data-target]').forEach((item) => {
              const rawMediaId = this.getRawMediaId(item.dataset.target);
              item.toggleAttribute('hidden', !allowPredicate(item, rawMediaId));
            });
          }
        };

        // Primary: strict media-id filtering when current media is a variant featured image.
        if (this.variantFeaturedMediaIds.has(activeRawMediaId)) {
          applyVisibility((item, rawMediaId) => {
            if (item.dataset.mediaId === sectionMediaId || item.dataset.target === sectionMediaId) return true;
            return isAllowedMedia(rawMediaId);
          });
          return;
        }

        // Fallback: filter by image alt color keywords based on selected color option.
        const colorContext = this.getColorOptionContext();
        if (!colorContext || colorContext.values.size === 0 || !colorContext.selected) return;

        const getItemAlt = (item) => {
          const image = item.querySelector('img[alt]');
          return (image?.getAttribute('alt') || '').toLowerCase();
        };

        applyVisibility((item) => {
          if (item.dataset.mediaId === sectionMediaId || item.dataset.target === sectionMediaId) return true;

          const alt = getItemAlt(item);
          if (!alt) return true;

          const containsAnyColorValue = Array.from(colorContext.values).some((value) => alt.includes(value));
          if (!containsAnyColorValue) return true;

          return alt.includes(colorContext.selected);
        });
      }

      isColorOptionName(name) {
        const normalized = (name || '').toLowerCase();
        return normalized.includes('color') || normalized.includes('colour') || normalized.includes('颜色');
      }

      getColorOptionContext() {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects) return null;

        // Radio/pill picker
        const allRadios = Array.from(variantSelects.querySelectorAll('input[type="radio"]'));
        const colorRadio = allRadios.find((input) => this.isColorOptionName(input.name));
        if (colorRadio) {
          const groupName = colorRadio.name;
          const values = new Set(
            allRadios
              .filter((input) => input.name === groupName)
              .map((input) => (input.value || '').trim().toLowerCase())
              .filter(Boolean)
          );
          const selected = (
            variantSelects.querySelector(`input[type="radio"][name="${CSS.escape(groupName)}"]:checked`)?.value || ''
          )
            .trim()
            .toLowerCase();
          return { selected, values };
        }

        // Dropdown picker
        const colorSelect = Array.from(variantSelects.querySelectorAll('select')).find((select) =>
          this.isColorOptionName(select.name)
        );
        if (colorSelect) {
          const values = new Set(
            Array.from(colorSelect.options)
              .map((option) => option.value.trim().toLowerCase())
              .filter(Boolean)
          );
          const selected = (colorSelect.value || '').trim().toLowerCase();
          return { selected, values };
        }

        return null;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
